import { inject, Injectable } from '@angular/core'
import { catchError, map, Observable, of, tap, throwError } from 'rxjs'
import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http'
import { UtilsService } from '@shared/services/utils.service'
import { UserHelperService } from '@modules/user-module/services/user-helper.service'
import { EnvironmentsHelperService } from '@environments/core/services/environments-helper.service'
import { WebsocketService } from '@modules/websocket-module/services/websocket.service'
import { PositionsMessageInterface } from '@modules/positions-module/interfaces/positions-message.interface'
import { PositionsInterface } from '@modules/positions-module/interfaces/positions.interface'
import { PositionsResponseInterface } from '@modules/positions-module/interfaces/positions-response.interface'
import { UserDetailInterface } from '@modules/user-module/interfaces/user-detail.interface'
import { ClosePositionInterface } from '@modules/positions-module/interfaces/close-position.interface'
import { OrderInterface } from '@modules/orders-module/interfaces/order.interface'
import { CreateOrderFromPositionInterface } from '@modules/stocks-module/orders/interfaces/create-order-from-position.interface'
import { PositionsStocksHelperService } from '@modules/stocks-module/positions/services/positions-stocks-helper.service'
import { PositionsStocksParamsInterface } from '@modules/stocks-module/positions/interfaces/positions-stocks-params.interface'
import { StocksOrdersHelperService } from '@modules/stocks-module/orders/services/stocks-orders-helper.service'

@Injectable({
	providedIn: 'root',
})
export class PositionsStocksService {
	private readonly _http: HttpClient = inject<HttpClient>(HttpClient)
	private readonly _ordersStocksHelperService: StocksOrdersHelperService =
		inject<StocksOrdersHelperService>(StocksOrdersHelperService)
	private readonly _utilsService: UtilsService = inject<UtilsService>(UtilsService)
	private readonly _userHelperService: UserHelperService = inject<UserHelperService>(UserHelperService)
	private readonly _positionsStocksHelperService: PositionsStocksHelperService =
		inject<PositionsStocksHelperService>(PositionsStocksHelperService)
	private readonly _environmentsHelperService: EnvironmentsHelperService =
		inject<EnvironmentsHelperService>(EnvironmentsHelperService)
	private readonly _websocketService: WebsocketService<PositionsMessageInterface, PositionsInterface[]> =
		inject<WebsocketService<PositionsMessageInterface, PositionsInterface[]>>(WebsocketService)


	public getAllPositions(): Observable<PositionsResponseInterface> {
		const positionsParams: PositionsStocksParamsInterface | null =
			this._positionsStocksHelperService.positionsParams$.getValue()

		const params: HttpParams = new HttpParams()
			.set('page', positionsParams?.page || 1)
			.set('page_size', positionsParams?.pageSize || 30)

		this._positionsStocksHelperService.toggleLoaderPositions(true)

		return this._http
			.get<PositionsResponseInterface>(`${this._environmentsHelperService.apiUrl}/alpaca/get_all_positions/`, {
				params,
			})
			.pipe(
				map((response: PositionsResponseInterface): PositionsResponseInterface => {
					return {
						...response,
						results: this._utilsService.sortArrayOfObjects<PositionsInterface>(
							response.results,
							'unrealized_plpc',
							'desc',
						),
					}
				}),
				tap((response: PositionsResponseInterface): void => {
					this._positionsStocksHelperService.positions$.next(response.results)
					this._positionsStocksHelperService.toggleLoaderPositions(false)
					this._positionsStocksHelperService.updateUnrealizedPL()
				}),
				catchError((error: HttpErrorResponse): never => {
					this._positionsStocksHelperService.toggleLoaderPositions(false)
					throw error
				}),
			)
	}

	public launchSocketPositions(): Observable<PositionsInterface[]> {
		const userDetail: UserDetailInterface | null = this._userHelperService.userDetail$.getValue()
		if (!userDetail) return of([])

		const url: string = `${this._environmentsHelperService.socketUrl}/ws/get_all_positions/`

		this._websocketService.connect(url)
		if (!this._websocketService.hasMessageInSocket(url)) {
			this._websocketService.sendMessage(url, {
				action: 'get_positions',
				request_id: this._utilsService.generateUniqId(),
				account_id: userDetail.broker_id,
			})
		}

		this._positionsStocksHelperService.toggleLoaderPositions(true)
		return this._websocketService.getResponses(url).pipe(
			map((positions: PositionsInterface[]): PositionsInterface[] => {
				return this._utilsService.sortArrayOfObjects<PositionsInterface>(positions, 'unrealized_plpc', 'desc')
			}),
			tap((positions: PositionsInterface[]): void => {
				this._positionsStocksHelperService.positions$.next(positions)
				this._positionsStocksHelperService.toggleLoaderPositions(false)
				this._positionsStocksHelperService.updateUnrealizedPL()
			}),
			catchError((error: HttpErrorResponse): never => {
				this._positionsStocksHelperService.toggleLoaderPositions(false)
				throw error
			}),
		)
	}

	public closePosition(order: ClosePositionInterface): Observable<OrderInterface> {
		return this._http
			.post<OrderInterface>(`${this._environmentsHelperService.apiUrl}/orders/close_positions/`, order)
			.pipe(
				tap((response: OrderInterface): void => {
					this._ordersStocksHelperService.moveOrder(response)
				}),
			)
	}

	public createOrderFromPosition(order: CreateOrderFromPositionInterface): Observable<OrderInterface> {
		if (this._userHelperService.checkIsLimited()) return throwError((): null => null)

		return this._http
			.post<OrderInterface>(`${this._environmentsHelperService.apiUrl}/orders/create_order_from_position/`, order)
			.pipe(
				tap((response: OrderInterface): void => {
					this._ordersStocksHelperService.moveOrder(response)
				}),
			)
	}
}
